// Single source of truth for the currencies Autumn can bill in, their Stripe
// minor-unit handling, and code validation. Deliberately scoped to Stripe's
// supported presentment currencies (not the full ISO 4217 set) so that:
//   1. the dashboard picker only offers currencies Stripe can actually charge,
//   2. server-side validation and the client picker agree exactly (a static
//      list, so no ICU-version drift between Bun and the browser).
// Source: https://docs.stripe.com/currencies. Codes are lowercase.

// Stripe zero-decimal currencies (no minor units — amount is already whole).
export const STRIPE_ZERO_DECIMAL_CURRENCIES = new Set([
	"bif",
	"clp",
	"djf",
	"gnf",
	"jpy",
	"kmf",
	"krw",
	"mga",
	"pyg",
	"rwf",
	"ugx",
	"vnd",
	"vuv",
	"xaf",
	"xof",
	"xpf",
]);

// Stripe three-decimal currencies (thousandths). Stripe does NOT list these as
// presentment currencies, so they are never offered by the picker or accepted
// by validation; this set exists only so the amount-conversion utils handle
// them correctly if they ever appear from imported/legacy Stripe data.
export const STRIPE_THREE_DECIMAL_CURRENCIES = new Set([
	"bhd",
	"jod",
	"kwd",
	"omr",
	"tnd",
]);

// Stripe's supported presentment currencies, verbatim from
// https://docs.stripe.com/currencies (verified 2026-07-06).
const STRIPE_PRESENTMENT_CURRENCIES = [
	"usd",
	"aed",
	"afn",
	"all",
	"amd",
	"ang",
	"aoa",
	"ars",
	"aud",
	"awg",
	"azn",
	"bam",
	"bbd",
	"bdt",
	"bif",
	"bmd",
	"bnd",
	"bob",
	"brl",
	"bsd",
	"bwp",
	"byn",
	"bzd",
	"cad",
	"cdf",
	"chf",
	"clp",
	"cny",
	"cop",
	"crc",
	"cve",
	"czk",
	"djf",
	"dkk",
	"dop",
	"dzd",
	"egp",
	"etb",
	"eur",
	"fjd",
	"fkp",
	"gbp",
	"gel",
	"gip",
	"gmd",
	"gnf",
	"gtq",
	"gyd",
	"hkd",
	"hnl",
	"htg",
	"huf",
	"idr",
	"ils",
	"inr",
	"isk",
	"jmd",
	"jpy",
	"kes",
	"kgs",
	"khr",
	"kmf",
	"krw",
	"kyd",
	"kzt",
	"lak",
	"lbp",
	"lkr",
	"lrd",
	"lsl",
	"mad",
	"mdl",
	"mga",
	"mkd",
	"mmk",
	"mnt",
	"mop",
	"mur",
	"mvr",
	"mwk",
	"mxn",
	"myr",
	"mzn",
	"nad",
	"ngn",
	"nio",
	"nok",
	"npr",
	"nzd",
	"pab",
	"pen",
	"pgk",
	"php",
	"pkr",
	"pln",
	"pyg",
	"qar",
	"ron",
	"rsd",
	"rub",
	"rwf",
	"sar",
	"sbd",
	"scr",
	"sek",
	"sgd",
	"shp",
	"sle",
	"sos",
	"srd",
	"std",
	"szl",
	"thb",
	"tjs",
	"top",
	"try",
	"ttd",
	"twd",
	"tzs",
	"uah",
	"ugx",
	"uyu",
	"uzs",
	"vnd",
	"vuv",
	"wst",
	"xaf",
	"xcd",
	"xcg",
	"xof",
	"xpf",
	"yer",
	"zar",
	"zmw",
];

// Common currencies surfaced first in the picker; the rest follow alphabetically.
const COMMON = ["usd", "eur", "gbp", "cad", "aud", "jpy", "chf", "cny", "inr"];
const COMMON_SET = new Set(COMMON);

export const CURRENCY_CODES: readonly string[] = [
	...COMMON,
	...STRIPE_PRESENTMENT_CURRENCIES.filter((c) => !COMMON_SET.has(c)).sort(),
];

const SUPPORTED_SET = new Set(CURRENCY_CODES);

export const isValidCurrencyCode = (code: string | null | undefined): boolean =>
	!!code && SUPPORTED_SET.has(code.toLowerCase());

// Stripe minor-unit multiplier: 1 (zero-decimal), 100 (default), 1000 (three-decimal).
export const stripeMinorUnitFactor = (
	code: string | null | undefined,
): number => {
	const key = (code ?? "usd").toLowerCase();
	if (STRIPE_ZERO_DECIMAL_CURRENCIES.has(key)) return 1;
	if (STRIPE_THREE_DECIMAL_CURRENCIES.has(key)) return 1000;
	return 100;
};

// Chargeable input precision. Stripe caps three-decimal currencies at two
// effective decimals (the integer amount must be divisible by ten), so the
// editor only ever offers 0 or 2 decimal places.
export const currencyInputDecimals = (
	code: string | null | undefined,
): number =>
	STRIPE_ZERO_DECIMAL_CURRENCIES.has((code ?? "usd").toLowerCase()) ? 0 : 2;

// Round an amount to the currency's chargeable precision, avoiding float drift.
export const roundToCurrencyPrecision = (
	amount: number,
	code: string | null | undefined,
): number => {
	const factor = 10 ** currencyInputDecimals(code);
	return Math.round(amount * factor) / factor;
};

// Static English names so the picker renders identically on every runtime.
// `Intl.DisplayNames` diverges by ICU version — e.g. older browsers lack the
// 2025 "XCG" name and would show a blank. Generated from ICU; keep in sync with
// STRIPE_PRESENTMENT_CURRENCIES.
const CURRENCY_NAMES: Record<string, string> = {
	usd: "US Dollar",
	eur: "Euro",
	gbp: "British Pound",
	cad: "Canadian Dollar",
	aud: "Australian Dollar",
	jpy: "Japanese Yen",
	chf: "Swiss Franc",
	cny: "Chinese Yuan",
	inr: "Indian Rupee",
	aed: "United Arab Emirates Dirham",
	afn: "Afghan Afghani",
	all: "Albanian Lek",
	amd: "Armenian Dram",
	ang: "Netherlands Antillean Guilder",
	aoa: "Angolan Kwanza",
	ars: "Argentine Peso",
	awg: "Aruban Florin",
	azn: "Azerbaijani Manat",
	bam: "Bosnia-Herzegovina Convertible Mark",
	bbd: "Barbadian Dollar",
	bdt: "Bangladeshi Taka",
	bif: "Burundian Franc",
	bmd: "Bermudan Dollar",
	bnd: "Brunei Dollar",
	bob: "Bolivian Boliviano",
	brl: "Brazilian Real",
	bsd: "Bahamian Dollar",
	bwp: "Botswanan Pula",
	byn: "Belarusian Ruble",
	bzd: "Belize Dollar",
	cdf: "Congolese Franc",
	clp: "Chilean Peso",
	cop: "Colombian Peso",
	crc: "Costa Rican Colón",
	cve: "Cape Verdean Escudo",
	czk: "Czech Koruna",
	djf: "Djiboutian Franc",
	dkk: "Danish Krone",
	dop: "Dominican Peso",
	dzd: "Algerian Dinar",
	egp: "Egyptian Pound",
	etb: "Ethiopian Birr",
	fjd: "Fijian Dollar",
	fkp: "Falkland Islands Pound",
	gel: "Georgian Lari",
	gip: "Gibraltar Pound",
	gmd: "Gambian Dalasi",
	gnf: "Guinean Franc",
	gtq: "Guatemalan Quetzal",
	gyd: "Guyanese Dollar",
	hkd: "Hong Kong Dollar",
	hnl: "Honduran Lempira",
	htg: "Haitian Gourde",
	huf: "Hungarian Forint",
	idr: "Indonesian Rupiah",
	ils: "Israeli New Shekel",
	isk: "Icelandic Króna",
	jmd: "Jamaican Dollar",
	kes: "Kenyan Shilling",
	kgs: "Kyrgyz Som",
	khr: "Cambodian Riel",
	kmf: "Comorian Franc",
	krw: "South Korean Won",
	kyd: "Cayman Islands Dollar",
	kzt: "Kazakhstani Tenge",
	lak: "Laotian Kip",
	lbp: "Lebanese Pound",
	lkr: "Sri Lankan Rupee",
	lrd: "Liberian Dollar",
	lsl: "Lesotho Loti",
	mad: "Moroccan Dirham",
	mdl: "Moldovan Leu",
	mga: "Malagasy Ariary",
	mkd: "Macedonian Denar",
	mmk: "Myanmar Kyat",
	mnt: "Mongolian Tugrik",
	mop: "Macanese Pataca",
	mur: "Mauritian Rupee",
	mvr: "Maldivian Rufiyaa",
	mwk: "Malawian Kwacha",
	mxn: "Mexican Peso",
	myr: "Malaysian Ringgit",
	mzn: "Mozambican Metical",
	nad: "Namibian Dollar",
	ngn: "Nigerian Naira",
	nio: "Nicaraguan Córdoba",
	nok: "Norwegian Krone",
	npr: "Nepalese Rupee",
	nzd: "New Zealand Dollar",
	pab: "Panamanian Balboa",
	pen: "Peruvian Sol",
	pgk: "Papua New Guinean Kina",
	php: "Philippine Peso",
	pkr: "Pakistani Rupee",
	pln: "Polish Zloty",
	pyg: "Paraguayan Guarani",
	qar: "Qatari Riyal",
	ron: "Romanian Leu",
	rsd: "Serbian Dinar",
	rub: "Russian Ruble",
	rwf: "Rwandan Franc",
	sar: "Saudi Riyal",
	sbd: "Solomon Islands Dollar",
	scr: "Seychellois Rupee",
	sek: "Swedish Krona",
	sgd: "Singapore Dollar",
	shp: "St. Helena Pound",
	sle: "Sierra Leonean Leone",
	sos: "Somali Shilling",
	srd: "Surinamese Dollar",
	std: "São Tomé & Príncipe Dobra",
	szl: "Swazi Lilangeni",
	thb: "Thai Baht",
	tjs: "Tajikistani Somoni",
	top: "Tongan Paʻanga",
	try: "Turkish Lira",
	ttd: "Trinidad & Tobago Dollar",
	twd: "New Taiwan Dollar",
	tzs: "Tanzanian Shilling",
	uah: "Ukrainian Hryvnia",
	ugx: "Ugandan Shilling",
	uyu: "Uruguayan Peso",
	uzs: "Uzbekistani Som",
	vnd: "Vietnamese Dong",
	vuv: "Vanuatu Vatu",
	wst: "Samoan Tala",
	xaf: "Central African CFA Franc",
	xcd: "East Caribbean Dollar",
	xcg: "Caribbean Guilder",
	xof: "West African CFA Franc",
	xpf: "CFP Franc",
	yer: "Yemeni Rial",
	zar: "South African Rand",
	zmw: "Zambian Kwacha",
};

export const currencyDisplayName = (code: string): string =>
	CURRENCY_NAMES[code.toLowerCase()] ?? code.toUpperCase();
